import { format } from "d3-format";
import PropTypes from "prop-types";
import { Chart, ChartCanvas } from "react-stockcharts";
import { XAxis, YAxis } from "react-stockcharts/lib/axes";
import { CrossHairCursor } from "react-stockcharts/lib/coordinates";
import { discontinuousTimeScaleProvider } from "react-stockcharts/lib/scale";
import { AreaSeries, LineSeries } from "react-stockcharts/lib/series";
import { last } from "react-stockcharts/lib/utils";
import { tooltipContent } from "../../exports/ChartProps";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import PriceEdgeIndicator from "./CustomChartComponents/EdgeIndicator/PriceEdgeIndicator";
import HoverTooltip from "./CustomChartComponents/HoverTooltip/HoverTooltip";
import LastPointIndicator from "./CustomChartComponents/LastPointEdgeIndicator/LastPointIndicator";
import PriceMarkerCoordinate from "./CustomChartComponents/PriceMarker/PriceMarkerCoordinate";

const PerformanceLineChart = ({
    data: chartData,
    height,
    width,
    ratio = 1,
    seriesConfig,
    yExtentsCalculator,
    onChartInteraction,
}) => {

    if (!chartData || chartData.length === 0) {
        return <></>;
    }

    function addDummyData(chartData, dummyCount = 10) {
        if (chartData.length < 2) {
            console.log("Not enough data to calculate interval.");
            return chartData;
        }

        // Step 1: find interval between last two dates
        const lastDate = new Date(chartData[chartData.length - 1].date);
        const prevDate = new Date(chartData[chartData.length - 2].date);

        const intervalMs = lastDate - prevDate;
        const intervalDays = intervalMs / (1000 * 60 * 60 * 24);


        // Step 2: generate dummy entries
        let newData = [...chartData];

        let currentDate = new Date(lastDate);

        for (let i = 1; i <= dummyCount; i++) {
            currentDate = new Date(currentDate.getTime() + intervalMs);

            const newEntry = {
                date: currentDate,
                originalDate: currentDate.toLocaleDateString('en-GB'), // e.g. "dd-mm-yyyy"
                timestamp: currentDate.getTime(),
            };

            newData.push(newEntry);
        }

        return newData;
    }

    const newChartData = addDummyData(chartData, 10);

    const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(
        (d) => d.date
    );
    const { data, xScale, xAccessor, displayXAccessor } =
        xScaleProvider(newChartData);
    const xExtents = [xAccessor(data[0]), xAccessor(last(data))];

    const DEVICE_SIZE = getDeviceSize();
    const margin = {
        left: 0,
        right: DEVICE_SIZE === "S" ? 0 : 10,
        top: 0,
        bottom: 0,
    };
    const gridHeight = height - margin.top - margin.bottom;
    const gridWidth = width - margin.left - margin.right;

    const yGrid = {
        innerTickSize: -1 * gridWidth,
        tickStrokeDasharray: "Solid",
        tickStrokeOpacity: 0.25,
        tickStrokeWidth: 0.1,
    };

    const xGrid = {
        innerTickSize: -1 * gridHeight,
        tickStrokeDasharray: "Solid",
        tickStrokeOpacity: 0.25,
        tickStrokeWidth: 0.1,
    };


    const primarySeries =
        seriesConfig.find((s) => s.isPrimary) || seriesConfig[0];
    const lastValue = primarySeries.yAccessor(last(data));


    return (
        <div className="chart-container">
            <ChartCanvas
                ratio={ratio}
                width={width}
                height={height}
                margin={{ left: 20, right: 30, top: 20, bottom: 20 }}
                type="svg"
                seriesName="CustomLineChart"
                data={data}
                xScale={xScale}
                xAccessor={xAccessor}
                displayXAccessor={displayXAccessor}
                xExtents={xExtents}
                mouseMoveEvent={true}
                panEvent={false}
                zoomEvent={false}
            >
                <Chart id={1} yExtents={yExtentsCalculator}>
                    <SharedChartComponents
                        DEVICE_SIZE={DEVICE_SIZE}
                        primarySeries={primarySeries}
                        lastValue={lastValue}
                        xGrid={xGrid}
                        yGrid={yGrid}
                    />

                    {seriesConfig.map((series, index) => {
                        const SeriesComponent =
                            series.type === "Area" ? AreaSeries : LineSeries;
                        return (
                            <SeriesComponent
                                key={index}
                                yAccessor={series.yAccessor}
                                stroke={series.stroke}
                                fill={series.fill}
                                strokeWidth={series.strokeWidth}
                                onChartInteraction={onChartInteraction}

                            />
                        );
                    })}
                </Chart>
                <CrossHairCursor stroke="#E0E0E0" />
            </ChartCanvas>
        </div>
    );
};

PerformanceLineChart.propTypes = {
    data: PropTypes.array.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    seriesConfig: PropTypes.arrayOf(
        PropTypes.shape({
            yAccessor: PropTypes.func.isRequired,
            stroke: PropTypes.string.isRequired,
            strokeWidth: PropTypes.number,
            type: PropTypes.oneOf(["Line", "Area"]),
            isPrimary: PropTypes.bool,
            fill: PropTypes.string,
        })
    ).isRequired,
    yExtentsCalculator: PropTypes.func.isRequired,
    ratio: PropTypes.number,
    onChartInteraction: PropTypes.func,
    hasMore: PropTypes.bool,
    onLoadMore: PropTypes.func,
};

function SharedChartComponents({
    DEVICE_SIZE,
    primarySeries,
    lastValue,
    xGrid,
    yGrid,
}) {
    const primaryColor = primarySeries.stroke;
    const primaryAccessor = primarySeries.yAccessor;

    return (
        <>
            <defs>
                <linearGradient id="customChartGradient" x1="0" y1="0" x2="0" y2="1">
                    {/* <stop offset="5%" stopColor={primaryColor} stopOpacity={0.1} /> */}
                    {/* <stop offset="95%" stopColor={primaryColor} stopOpacity={0} /> */}
                </linearGradient>
            </defs>

            <AreaSeries
                yAccessor={primaryAccessor}
                strokeWidth={0}
                fill="url(#customChartGradient)"
            />
            <LastPointIndicator
                yAccessor={primaryAccessor}
                displayFormat={format(".4s")}
                radius={DEVICE_SIZE === "S" ? 6 : 3}
                fill={primaryColor}
            />
            <PriceMarkerCoordinate
                at="left"
                orient="right"
                price={lastValue}
                displayFormat={format(".2f")}
                dx={20}
                fill="#d8d8d8"
                textFill={COLOR_VARS["SWIFT_COLOR4"]}
                lineStroke="#404040"
                lineOpacity={0.15}
                rectWidth={65}
                rectHeight={22}
                fontSize={11}
                fontWeight="700"
                opacity={0.6}
            />
            <HoverTooltip
                tooltipContent={tooltipContent()}
                fontSize={11}
                bgOpacity={0}
                fill="#ffffff"
                opacity={1}
                bgrx={15}
                stroke="none"
                isLabled={false}
                isInline={true}
            />
            {DEVICE_SIZE !== "S" && (
                <>
                    <YAxis
                        axisAt="right"
                        orient="right"
                        showTicks={true}
                        showTickLabel={true}
                        showDomain={false}
                        outerTickSize={0}
                        innerTickSize={0}
                        fontSize={12}
                        ticks={10}
                        tickPadding={4}
                        tickStroke={COLOR_VARS["SWIFT_COLOR4"]}
                        {...yGrid}
                    />
                    <PriceEdgeIndicator
                        orient="left"
                        edgeAt="right"
                        itemType="last"
                        yAccessor={primaryAccessor}
                        displayFormat={format(".2f")}
                        arrowWidth={0}
                        fill="#efefef"
                        fontSize={11}
                        rectWidth={70}
                        rectHeight={22}
                        textFill={COLOR_VARS["SWIFT_COLOR4"]}
                        strokeWidth={3}
                        lineOpacity={0}
                        opacity={1}
                        rectRadius={14}
                        fontWeight="700"
                        dx={20}
                    />
                </>
            )}
            <XAxis
                axisAt="bottom"
                orient="top"
                showTicks={true}
                showTickLabel={true}
                showDomain={false}
                outerTickSize={0}
                innerTickSize={0}
                fontSize={12}
                fontWeight={500}
                ticks={DEVICE_SIZE === "S" ? 4 : 6}
                tickPadding={6}
                fill="red"
                opacity={1}
                tickStroke={COLOR_VARS["SWIFT_COLOR4"]}
                {...xGrid}
            />
        </>
    );
}

export default PerformanceLineChart;
